/**
 * @file Table review surface for a whole-document mapping review: one section
 * per `TablePlan` (keyed by group id). Each section heads with its source
 * identity (provenance + "Table n of total"), the target-table assignment
 * review — the retarget Select over the bound document's tables
 * (`useTargetFields().tables`), the shared confidence Tag, a per-table source-
 * section selector (`MappingReviewChunkNav`) and a Skip/Keep (exclude/include)
 * toggle — and the shared column / bulk / row editors against that slice. A
 * retarget invalidates the model's column plans (they mapped to the old table's
 * fields), so the slice's columns become user-decided only; the section
 * reflects that. Sections needing work (unmapped columns or a low-confidence
 * assignment) carry a warning border and start expanded; clean ones auto-
 * collapse so attention goes where it is needed. A mini-TOC
 * (`MappingReviewTableToc`) jumps between sections and tracks review progress.
 * Excluded groups collapse to the header with a Keep action (disable, never
 * hide). Every editable control freezes while an approval is being written.
 */
import { FC, useState } from 'react';
import {
  Button,
  Select,
  Skeleton,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import {
  DownOutlined,
  RightOutlined,
  StopOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import type {
  MappingTarget,
  RowClassificationTag,
  TableAlternative,
} from '#types';
import FlexBox from '../../atoms/FlexBox';
import FlexCol from '../../atoms/FlexCol';
import {
  isWholeDocument,
  removeTable,
  resetTableTarget,
  restoreTable,
  setTableTarget,
} from './mappingReviewSession';
import type {
  MappingReviewSession,
  MappingReviewTableSlice,
} from './mappingReviewState';
import { mappingReviewConfidence } from './mappingReviewConfidence';
import { mappingReviewLabels } from './mappingReviewLabels';
import { mappingReviewRankedOptions } from './mappingReviewRankedOptions';
import { useMappingReviewSubmitting } from './useMappingReviewSubmitting';
import { useTargetFields } from './useTargetFields';
import { useTableTypeFields } from './useTableTypeFields';
import MappingReviewChunkNav from './MappingReviewChunkNav';
import { mappingReviewDescribeSource } from './mappingReviewDescribeSource';
import MappingReviewConfidenceTag from './MappingReviewConfidenceTag';
import MappingReviewTableToc from './MappingReviewTableToc';
import MappingReviewColumns from './MappingReviewColumns';
import type { ReferenceValuesTarget } from './MappingReviewColumns';
import MappingReviewBulkBar from './MappingReviewBulkBar';
import MappingReviewRows from './MappingReviewRows';

const { Text, Title } = Typography;

/**
 * Per-table glanceable status, derived live from the slice + its `TablePlan`.
 * `reviewed` is a proxy (no persisted per-table review state): assigned, every
 * source column mapped, and a table-assignment confidence at or above the warn
 * threshold. `needsAttention` additionally flags a low-confidence assignment,
 * driving the warning border and auto-expand.
 */
export interface MappingReviewTableStatus {
  groupId: string;
  ordinal: number;
  total: number;
  provenance: string;
  assignmentLabel: string;
  targetTableId: string;
  removed: boolean;
  retargeted: boolean;
  manual: boolean;
  unmappedCount: number;
  needsAttention: boolean;
  reviewed: boolean;
  band: 'excluded' | 'attention' | 'reviewed';
}

const sectionDomId = (groupId: string): string =>
  `mapping-review-table-${groupId}`;

interface MappingReviewTablesProps {
  session: MappingReviewSession;
  target: Extract<MappingTarget, { kind: 'document' }>;
  tagFilter: RowClassificationTag | null;
  referenceValues: ReferenceValuesTarget | null;
  onOpenReferenceValues: (target: ReferenceValuesTarget) => void;
  onCloseReferenceValues: () => void;
}

const TableSection: FC<{
  slice: MappingReviewTableSlice;
  status: MappingReviewTableStatus;
  tables: { tableId: string; label: string }[];
  planTableId: string | undefined;
  planConfidence: number | undefined;
  planAlternatives: TableAlternative[];
  domId: string;
  tagFilter: RowClassificationTag | null;
  referenceValues: ReferenceValuesTarget | null;
  onOpenReferenceValues: (target: ReferenceValuesTarget) => void;
  onCloseReferenceValues: () => void;
}> = ({
  slice,
  status,
  tables,
  planTableId,
  planConfidence,
  planAlternatives,
  domId,
  tagFilter,
  referenceValues,
  onOpenReferenceValues,
  onCloseReferenceValues,
}) => {
  const { token } = theme.useToken();
  const submitting = useMappingReviewSubmitting();
  const { loading, fields } = useTableTypeFields(status.targetTableId);
  const [open, setOpen] = useState(status.needsAttention || status.total === 1);
  const sliceReferenceValues =
    referenceValues?.groupId === slice.groupId ? referenceValues : null;
  const sourceColumns = [
    ...new Set(slice.chunks.flatMap((c) => c.sourceColumns)),
  ];
  const aiScore = status.retargeted
    ? planAlternatives.find((a) => a.tableId === status.targetTableId)
        ?.probability
    : undefined;

  const tableOptions = mappingReviewRankedOptions({
    scored: [
      ...(planTableId !== undefined && planConfidence !== undefined
        ? [{ value: planTableId, probability: planConfidence }]
        : []),
      ...planAlternatives.map((a) => ({
        value: a.tableId,
        probability: a.probability,
      })),
    ],
    universe: tables.map((t) => ({ value: t.tableId, label: t.label })),
    universeLabel: 'All tables',
  });

  const bodyVisible = !status.removed && open;

  return (
    <FlexCol
      noGrow
      gap={10}
      id={domId}
      tabIndex={-1}
      style={{
        border: `1px solid ${status.needsAttention ? token.colorWarningBorder : token.colorBorderSecondary}`,
        borderRadius: token.borderRadius,
        padding: 10,
      }}
    >
      <FlexCol noGrow gap={5}>
        <FlexBox noGrow stretch gap={5}>
          <FlexBox noGrow justifyStart gap={5}>
            <Button
              type="text"
              size="small"
              icon={open ? <DownOutlined /> : <RightOutlined />}
              disabled={status.removed}
              aria-label={`${open ? 'Collapse' : 'Expand'} ${status.provenance}`}
              onClick={() => setOpen((v) => !v)}
            />
            <Title level={5} style={{ margin: 0 }}>
              {status.provenance} · Table {status.ordinal} of {status.total}
            </Title>
            {status.removed ? (
              <Tag style={{ margin: 0 }}>{mappingReviewLabels.skip}</Tag>
            ) : (
              <MappingReviewConfidenceTag
                manual={status.manual}
                confidence={planConfidence}
                aiScore={aiScore}
              />
            )}
            {!status.removed && status.unmappedCount > 0 && (
              <Tag color="warning" style={{ margin: 0 }}>
                {status.unmappedCount} unmapped
              </Tag>
            )}
          </FlexBox>
          <FlexBox noGrow justifyEnd gap={5}>
            <Tooltip title="Restore this group's AI table assignment and column plan">
              <Button
                type="text"
                size="small"
                icon={<UndoOutlined />}
                disabled={submitting}
                aria-label={`Reset table assignment for ${slice.groupId}`}
                onClick={() => resetTableTarget(slice.groupId)}
              />
            </Tooltip>
            <Button
              type="text"
              size="small"
              icon={status.removed ? <UndoOutlined /> : <StopOutlined />}
              disabled={submitting}
              aria-label={
                status.removed
                  ? `${mappingReviewLabels.keep} table ${slice.groupId}`
                  : `${mappingReviewLabels.skip} table ${slice.groupId}`
              }
              onClick={() =>
                status.removed
                  ? restoreTable(slice.groupId)
                  : removeTable(slice.groupId)
              }
            >
              {status.removed
                ? mappingReviewLabels.keep
                : mappingReviewLabels.skip}
            </Button>
          </FlexBox>
        </FlexBox>
        <FlexBox noGrow stretch wrap alignStart gap={10}>
          <FlexBox noGrow justifyStart gap={5}>
            <Text type="secondary">Target table</Text>
            <Select
              size="small"
              showSearch={{ optionFilterProp: 'label' }}
              aria-label={`Target table for ${slice.groupId}`}
              value={status.targetTableId}
              disabled={status.removed || loading || submitting}
              options={tableOptions}
              onChange={(value: string) => setTableTarget(slice.groupId, value)}
              style={{ minWidth: 'min(350px, 100%)' }}
            />
          </FlexBox>
          {bodyVisible && <MappingReviewChunkNav slice={slice} />}
        </FlexBox>
        {status.retargeted && (
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            Retargeted to &quot;{status.assignmentLabel}&quot; — the AI column
            plan no longer applies; map the columns below.
          </Text>
        )}
      </FlexCol>
      {bodyVisible &&
        (loading ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : sourceColumns.length === 0 ? (
          <Text type="secondary">This table has no columns to map.</Text>
        ) : (
          <>
            <MappingReviewColumns
              slice={slice}
              targetFields={fields}
              fieldsLoading={loading}
              referenceValues={sliceReferenceValues}
              onOpenReferenceValues={onOpenReferenceValues}
              onCloseReferenceValues={onCloseReferenceValues}
            />
            <FlexBox noGrow justifyStart>
              <MappingReviewBulkBar slice={slice} />
            </FlexBox>
            <MappingReviewRows
              slice={slice}
              targetFields={fields}
              tagFilter={tagFilter}
              onOpenReferenceValues={onOpenReferenceValues}
            />
          </>
        ))}
    </FlexCol>
  );
};

const MappingReviewTables: FC<MappingReviewTablesProps> = ({
  session,
  target,
  tagFilter,
  referenceValues,
  onOpenReferenceValues,
  onCloseReferenceValues,
}) => {
  const snap = useSnapshot(session);
  const { tables } = useTargetFields(target);
  const baseDoc = session.baseDoc;
  const planByGroup = new Map(
    baseDoc && isWholeDocument(baseDoc)
      ? baseDoc.tables.map((t) => [t.ref.id, t] as const)
      : [],
  );

  if (snap.tableOrder.length === 0) {
    return (
      <Text type="secondary" className="mapping-review-tables">
        No tables were extracted for this document.
      </Text>
    );
  }

  const total = snap.tableOrder.length;
  const statuses: MappingReviewTableStatus[] = snap.tableOrder.map(
    (groupId, i) => {
      const sliceSnap = snap.tables[groupId];
      const plan = planByGroup.get(groupId);
      const removed = sliceSnap.targetRemoved;
      const targetTableId = sliceSnap.targetTableId;
      const planTableId = plan?.tableId;
      const retargeted =
        !removed && planTableId !== undefined && targetTableId !== planTableId;
      const confidence = plan?.confidence;
      const confidenceWarn =
        !retargeted &&
        confidence !== undefined &&
        mappingReviewConfidence(confidence).warn;
      const sourceColumns = new Set(
        sliceSnap.chunks.flatMap((c) => c.sourceColumns),
      );
      const mapped = new Set(sliceSnap.columns.map((c) => c.sourceColumn));
      const unmappedCount = [...sourceColumns].filter(
        (c) => !mapped.has(c),
      ).length;
      const needsAttention = !removed && (unmappedCount > 0 || confidenceWarn);
      const firstSource = sliceSnap.chunks[0]?.source;
      return {
        groupId,
        ordinal: i + 1,
        total,
        provenance: firstSource
          ? mappingReviewDescribeSource(firstSource)
          : 'Table',
        assignmentLabel:
          tables.find((t) => t.tableId === targetTableId)?.label ??
          targetTableId,
        targetTableId,
        removed,
        retargeted,
        manual: retargeted || confidence === undefined,
        unmappedCount,
        needsAttention,
        reviewed: !removed && !needsAttention,
        band: removed ? 'excluded' : needsAttention ? 'attention' : 'reviewed',
      };
    },
  );
  const reviewedCount = statuses.filter((s) => s.reviewed).length;

  const jumpToTable = (groupId: string): void => {
    const el = document.getElementById(sectionDomId(groupId));
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    el?.focus({ preventScroll: true });
  };

  return (
    <FlexCol noGrow gap={10} className="mapping-review-tables">
      {total > 1 && (
        <MappingReviewTableToc
          statuses={statuses}
          reviewedCount={reviewedCount}
          onJump={jumpToTable}
        />
      )}
      {statuses.map((status) => {
        const plan = planByGroup.get(status.groupId);
        return (
          <TableSection
            key={status.groupId}
            slice={session.tables[status.groupId]}
            status={status}
            tables={tables}
            planTableId={plan?.tableId}
            planConfidence={plan?.confidence}
            planAlternatives={plan?.alternatives ?? []}
            domId={sectionDomId(status.groupId)}
            tagFilter={tagFilter}
            referenceValues={referenceValues}
            onOpenReferenceValues={onOpenReferenceValues}
            onCloseReferenceValues={onCloseReferenceValues}
          />
        );
      })}
    </FlexCol>
  );
};

export default MappingReviewTables;
